import React, { useState } from 'react';

import fileDownload from 'react-file-download';

import {
  Alert,
  Box,
  Button,
  ButtonProps,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import ErrorDisplay from '~/components/Common/ErrorDisplay';
import Spinner from '~/components/Common/Spinner';

import { HOTEL_OFFER_TYPES } from '~/consts/offerTypes';
import { PROPERTY_CHANNEL_MANAGERS } from '~/consts/reservation';

import ReservationService from '~/services/ReservationService';

interface Props {
  vendorId: string;
  propertyId: string;
  offers?: Array<Pick<App.Offer, 'id_salesforce_external' | 'name' | 'type'>>;
  channelManager?: string;
  hideTitle?: boolean;
  buttonProps?: Pick<ButtonProps, 'disabled' | 'fullWidth' | 'startIcon' | 'variant'>;
  onImported?: () => void;
}

export default function PropertyUploadData(props: Props) {
  const { buttonProps, hideTitle = false } = props;
  const { vendorId, propertyId, channelManager, onImported } = props;
  const offers = (props.offers ?? []).filter((offer) => HOTEL_OFFER_TYPES.includes(offer.type));
  const [createPackages, setCreatePackages] = useState<boolean>(true);
  const [packageOfferId, setPackageOfferId] = useState<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [inProgress, setInProgress] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [showConfirmationMessage, setShowConfirmationMessage] = useState<boolean>(false);
  const supportsPullingOccupancies = channelManager === PROPERTY_CHANNEL_MANAGERS.derbysoft;
  const showForm = !inProgress && !showConfirmationMessage;

  const handleOpen = () => {
    setFile(null);
    setCreatePackages(true);
    setPackageOfferId(offers[0]?.id_salesforce_external ?? '');
    setError(null);
    setWarning(null);
    setShowConfirmationMessage(false);
    setShowModal(true);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await ReservationService.downloadPropertyImportTemplate();
      fileDownload(response, 'property-import-template.xlsx');
    } catch (e) {
      setError(e.errors?.[0]?.message ?? e.message);
    }
  };

  const handleSubmit = async () => {
    setInProgress(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('vendorId', vendorId);
    if (createPackages && packageOfferId) {
      formData.append('offerId', packageOfferId);
    }

    try {
      const response = await ReservationService.importPropertiesData(propertyId, formData);
      let warningMessage = response?.message ?? null;
      // Automatically pull occupancy data for derbysoft properties
      if (supportsPullingOccupancies) {
        try {
          await ReservationService.triggerHotelUpdates(propertyId);
        } catch (e) {
          warningMessage = [warningMessage, `Imported, but pulling occupancies failed: ${e.message}`]
            .filter(Boolean)
            .join(' ');
        }
      }
      setWarning(warningMessage);
      setShowConfirmationMessage(true);
      onImported?.();
    } catch (e) {
      setError(e.errors?.[0]?.message ?? e.message);
    } finally {
      setInProgress(false);
    }
  };

  return (
    <div>
      <div>
        {!hideTitle && (
          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
            Property Data
          </Typography>
        )}
        <Button onClick={handleOpen} type="button" variant="contained" {...buttonProps}>
          Upload Data File
        </Button>
      </div>

      <Dialog open={showModal} onClose={() => !inProgress && setShowModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload property data</DialogTitle>
        <DialogContent>
          {showForm && (
            <Stack spacing={2}>
              <DialogContentText>
                Creates new rooms and vendor rate plans from rows matching this property's HotelCode, and links them.
                Existing ones are kept. Also replaces the property's address and coordinates.
              </DialogContentText>
              {offers.length > 0 && (
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Checkbox checked={createPackages} onChange={(e) => setCreatePackages(e.target.checked)} />
                    }
                    label="Create packages from upload"
                  />
                  {createPackages && (
                    <TextField
                      label="Offer"
                      value={packageOfferId}
                      onChange={(e) => setPackageOfferId(e.target.value)}
                      helperText="Each room in the sheet gets a content-approved package on this offer with all its rates."
                      select
                      fullWidth
                    >
                      {offers.map((offer) => (
                        <MenuItem key={offer.id_salesforce_external} value={offer.id_salesforce_external}>
                          {offer.name} ({offer.id_salesforce_external})
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Stack>
              )}
              <Box>
                <Button variant="outlined" size="small" onClick={handleDownloadTemplate}>
                  Download template (.xlsx)
                </Button>
              </Box>
              <TextField
                label="File"
                type="file"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFile(e.target.files?.[0] ?? null)}
                inputProps={{
                  accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel',
                }}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
              />
            </Stack>
          )}

          {inProgress && (
            <>
              <DialogContentText>Please wait while the data file is being uploaded.</DialogContentText>
              <Spinner />
            </>
          )}
          {error && <ErrorDisplay message={error} />}
          {showConfirmationMessage && warning && <Alert severity="warning">{warning}</Alert>}
          {showConfirmationMessage && !warning && (
            <Alert severity="success">
              <strong>Success!</strong> Imported
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowModal(false)} disabled={inProgress}>
            {showForm ? 'Cancel' : 'Close'}
          </Button>
          {showForm && (
            <Button variant="contained" onClick={handleSubmit} disabled={!file}>
              Submit
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}
